import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { IMapGeoObject, Map as NewbuildingMapComponent } from '@cian/frontend-newbuilding-map-component';

import {
  POSSIBLE_OUTSIDE_INFRASTRUCTURE_TYPES,
  TRANSPORT_ACCESSIBILITY_SIDEBAR_WIDTH,
} from '../../../constants/infrastructure';
import { getGeoCoordinates } from '../../../selectors/newbuilding';
import { EOutsideTAInfrastructureType, TTAInfrastructureItem } from '../../../types/map';
import { getRouteLabel } from '../helpers';

export const useTransportAccessibilityRouting = (mapApi: NewbuildingMapComponent | null) => {
  const routeIdRef = useRef<string>();
  const routeLabelRef = useRef<IMapGeoObject>();

  const mainPinCoordinates = useSelector(getGeoCoordinates);

  const setTARouting = useCallback(
    async (item: TTAInfrastructureItem) => {
      if (!mainPinCoordinates) {
        return;
      }

      const routeId = `customRoute-${item.id}`;
      routeIdRef.current = routeId;

      await mapApi?.addRoute(
        routeId,
        { coordinates: mainPinCoordinates },
        { coordinates: item.coordinates },
        'pedestrian',
        [0, 0, 0, TRANSPORT_ACCESSIBILITY_SIDEBAR_WIDTH],
      );

      if (POSSIBLE_OUTSIDE_INFRASTRUCTURE_TYPES.includes(item.type as EOutsideTAInfrastructureType) && item.title) {
        const routeLabelGeoObject = getRouteLabel({
          id: `${routeId}-label`,
          coordinates: item.coordinates,
          text: item.title,
        });

        routeLabelRef.current = routeLabelGeoObject;
        mapApi?.addGeoObjects([routeLabelGeoObject]);
      }
    },
    [mapApi, mainPinCoordinates],
  );

  const resetTARouting = useCallback(() => {
    if (routeIdRef.current) {
      mapApi?.removeRoute(routeIdRef.current);
      routeIdRef.current = undefined;

      if (routeLabelRef.current) {
        mapApi?.removeGeoObjects([routeLabelRef.current], false);
        routeLabelRef.current = undefined;
      }
    }
  }, [mapApi]);

  return {
    setTARouting,
    resetTARouting,
  };
};
